import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, LabelList
} from 'recharts';
import { getQuestionStats } from '../../services/adminApi';

const QuestionStats = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  

  const COLORS = {
    difficulty: {
      easy: '#28a745',
      medium: '#ffc107',
      hard: '#dc3545'
    },
    
  };

  // Custom label for difficulty pie chart: place labels outside and hide tiny slices
  const renderDifficultyLabel = ({ cx, cy, midAngle, outerRadius, percent, name, value }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 16; // distance from center for label
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Hide labels for slices smaller than 1% to avoid overlap
    if ((percent || 0) * 100 < 1) return null;

    const anchor = x > cx ? 'start' : 'end';
    return (
      <text x={x} y={y} fill="#333" textAnchor={anchor} dominantBaseline="central" fontSize={12}>
        {`${name}: ${value} (${((percent || 0) * 100).toFixed(1)}%)`}
      </text>
    );
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const data = await getQuestionStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  

  const handleCardClick = (filterType, filterValue) => {
    // Navigate to questions list with filter applied
    const params = new URLSearchParams();
    params.set(filterType, filterValue);
    navigate(`/admin/questions?${params.toString()}`);
  };

  // Transform data for charts
  const getDifficultyChartData = () => {
    if (!stats || !stats.by_difficulty) return [];
    return Object.entries(stats.by_difficulty).map(([key, value]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      value: value,
      color: COLORS.difficulty[key]
    }));
  };

  const getTopTopicsChartData = () => {
    if (!stats || !stats.top_topics) return [];
    return Object.entries(stats.top_topics)
      .map(([topic, count]) => ({
        key: topic,
        name: topic.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        count: count
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger">Failed to load statistics</div>
      </div>
    );
  }

  const difficultyData = getDifficultyChartData();
  const topTopicsData = getTopTopicsChartData();

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">
          <i className="fas fa-chart-bar mr-2"></i>
          Question Statistics
        </h1>
        <div>
          <Link
            to="/admin"
            className="btn btn-sm btn-secondary mr-2"
          >
            <i className="fas fa-arrow-left mr-1"></i>
            Back to Dashboard
          </Link>
          <Link
            to="/admin/questions"
            className="btn btn-sm btn-primary"
          >
            <i className="fas fa-list mr-1"></i>
            View All Questions
          </Link>
        </div>
      </div>

      

      {/* Summary Cards */}
      <div className="row">
        {/* Total Questions */}
        <div className="col-xl-3 col-md-6 mb-4">
          <div
            className="card border-left-primary shadow h-100 py-2"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/admin/questions')}
          >
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                    Total Questions
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {stats.total_questions || 0}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-question-circle fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Easy Questions */}
        <div className="col-xl-3 col-md-6 mb-4">
          <div
            className="card border-left-success shadow h-100 py-2"
            style={{ cursor: 'pointer' }}
            onClick={() => handleCardClick('difficulty', 'easy')}
          >
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                    Easy Questions
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {stats.by_difficulty?.easy || 0}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-smile fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Medium Questions */}
        <div className="col-xl-3 col-md-6 mb-4">
          <div
            className="card border-left-warning shadow h-100 py-2"
            style={{ cursor: 'pointer' }}
            onClick={() => handleCardClick('difficulty', 'medium')}
          >
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                    Medium Questions
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {stats.by_difficulty?.medium || 0}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-meh fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hard Questions */}
        <div className="col-xl-3 col-md-6 mb-4">
          <div
            className="card border-left-danger shadow h-100 py-2"
            style={{ cursor: 'pointer' }}
            onClick={() => handleCardClick('difficulty', 'hard')}
          >
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-danger text-uppercase mb-1">
                    Hard Questions
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {stats.by_difficulty?.hard || 0}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-frown fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row - Combined for better space utilization */}
      <div className="row">
        {/* Difficulty Distribution Pie Chart */}
        <div className="col-xl-4 col-lg-5 mb-4">
          <div className="card shadow h-100">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">
                Questions by Difficulty
              </h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={420}>
                <PieChart>
                  <Pie
                    data={difficultyData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={renderDifficultyLabel}
                    outerRadius={90}
                    minAngle={5}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {difficultyData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Top Topics Chart */}
        <div className="col-xl-8 col-lg-7 mb-4">
          <div className="card shadow h-100">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">
                Top 10 Topics
              </h6>
            </div>
            <div className="card-body">
              {topTopicsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={420}>
                  <BarChart
                    data={topTopicsData}
                    layout="vertical"
                    margin={{ top: 10, right: 30, left: 150, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={10} tick={false} axisLine={false} tickLine={false} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="count"
                      fill="#00C49F"
                      name="Question Count"
                      onClick={(data) => handleCardClick('topic', data.payload?.key)}
                      style={{ cursor: 'pointer' }}
                    >
                      <LabelList dataKey="name" position="left" offset={10} style={{ fontSize: 12, fill: '#333' }} />
                      <LabelList dataKey="count" position="right" offset={8} style={{ fontSize: 12, fill: '#333' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-inbox fa-3x mb-3"></i>
                  <p>No topic data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionStats;
